import React, { memo, useCallback, useState, useMemo } from 'react'
import { Route, useHistory } from 'react-router-dom'
import compact from 'lodash/compact'
import times from 'lodash/times'
import Card from 'transfix-ui/components/Card'
import { Pagination } from 'transfix-ui/components/Pagination'
import { makeStyles, Skeleton, Typography } from 'transfix-ui/core'
import { IOfferedShipmentFilter } from 'types/FMSGraphqlTypes'
import { MarketplaceLoadDetailsModalContainer } from 'apps/fleet-planner/pages/marketplace/MarketplaceLoadDetailsContainer'
import { TOTAL_LOADS_PER_PAGE } from '../../constants'
import { IOfferFragment } from '../../graphql/OfferFragment'
import { useOfferedShipmentsQuery } from '../../graphql/OfferedShipments'
import ShipmentOfferRow from '../ShipmentOfferRow'

export interface IShipmentOffersTabProps {
  filter: IOfferedShipmentFilter
  onDeclineSuccess?: () => void
}

const useShipmentOffersTabStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  skeleton: {
    height: 73,
  },
  emptyState: {
    textAlign: 'center',
  },
  pagination: {
    textAlign: 'center',
  },
}))

const ShipmentOffersTab = memo<IShipmentOffersTabProps>(({ filter, onDeclineSuccess }) => {
  const classes = useShipmentOffersTabStyles()
  const history = useHistory()
  const [page, setPage] = useState<number>(1)

  const { data, loading, refetch } = useOfferedShipmentsQuery({
    variables: { filter, first: TOTAL_LOADS_PER_PAGE, before: null, after: null },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })
  const { offeredShipmentConnection } = data || {}
  const { nodes = [], pageInfo, totalCount = 0 } = offeredShipmentConnection || {}
  const { endCursor = '', startCursor = '' } = pageInfo || {}
  const shipments = compact<IOfferFragment>(nodes)

  const pageChangeHandler = useCallback(
    (nextPage: number) => {
      if (nextPage > page) {
        refetch({ filter, before: null, after: endCursor })
      } else if (nextPage < page) {
        refetch({ filter, before: startCursor, after: null })
      }
      setPage(nextPage)
    },
    [endCursor, filter, page, refetch, startCursor]
  )

  const declineSuccessHandler = useCallback(() => {
    if (onDeclineSuccess) {
      onDeclineSuccess()
    }
    refetch({ filter, before: null, after: null })
    setPage(1)
  }, [filter, onDeclineSuccess, refetch])

  const closeModalHandler = useCallback(() => {
    history.replace(`/offers${history.location.search}`)
  }, [history])

  const isUnavailable = useMemo(() => {
    return filter === IOfferedShipmentFilter.past
  }, [filter])

  const showRows = useMemo(() => {
    return !loading && shipments.length > 0
  }, [loading, shipments.length])

  const showEmpty = useMemo(() => {
    return !loading && shipments.length === 0
  }, [loading, shipments.length])

  const numberOfPages = useMemo(() => {
    return Math.ceil(totalCount / TOTAL_LOADS_PER_PAGE)
  }, [totalCount])

  return (
    <div className={classes.container}>
      {loading &&
        times(4).map(i => <Skeleton key={i} variant='rectangular' className={classes.skeleton} />)}
      {showRows &&
        shipments.map(shipment => {
          return (
            <ShipmentOfferRow
              key={shipment.id}
              disabled={isUnavailable}
              offeredShipment={shipment}
              onDeclineSuccess={declineSuccessHandler}
              showBorder
            />
          )
        })}
      {showEmpty && (
        <Card>
          <Typography variant='subtitle1' color='textSecondary' className={classes.emptyState}>
            No {isUnavailable ? 'Unavailable' : 'Available'} Offers
          </Typography>
        </Card>
      )}
      {numberOfPages > 1 && (
        <div className={classes.pagination}>
          <Pagination
            currentPage={page}
            numberOfPages={numberOfPages}
            onPageChange={pageChangeHandler}
            displayPageNumbers={false}
          />
        </div>
      )}
      <Route exact path='/offers/:shipmentId'>
        <MarketplaceLoadDetailsModalContainer
          route='offers'
          onModalClose={closeModalHandler}
          onShipmentDecline={declineSuccessHandler}
        />
      </Route>
    </div>
  )
})

export default ShipmentOffersTab
